import { ImageType } from "@/types/baseTypes";
import { Modul_Linkliste } from "@/wp/types/acf-module-types";

// for more infomation visit https://api.billetto.com/reference/event
export type BillettoEventType = {
  id: string;
  object: "event";
  name: string;
  currency: "EUR" | "USD" | "GBP"; // ISO 4217 currency code
  state:
    | "cancelled"
    | "completed"
    | "deleted"
    | "draft"
    | "published"
    | "publishing";
  public: boolean;
  online_event: boolean;
  image_url: string;
  public_url: string;
  starts_at: string;
  ends_at: string;
  editorial: {
    host: string;
    category: string;
    type: string;
    description: string;
    description_html: string;
  };
  location: {
    id: string;
    name: string;
    address_line_1: string;
    address_line_2: string;
    postal_code: string;
    city: string;
  };
  ticket_types: BillettoGetResponse<BilletoEventTicketType[]>;
};

export type BilletoEventTicketType = {
  id: string;
  object: "ticket_type";
  uuid: string;
  type:
    | "FreeTicketType"
    | "PayTicketType"
    | "AttendanceTicketType"
    | "NoPdfPayTicketType"
    | "DonationTicketType";
  name: string;
  description: string;
  price: number;
};

export type BilletoEventOverviewType = Pick<
  BillettoEventType,
  "id" | "name" | "starts_at" | "location"
>;

type BillettoGetResponse<T> = {
  object: "list";
  data: T;
  has_more: boolean;
  total: number;
  url: string;
};

type BillettoFetchOptionsType = Partial<{
  limit: number;
  startsAfter: Date;
  startBefore: Date;
  state: BillettoEventType["state"];
  expand: string;
}>;

type FetchAllReturnType = Promise<BillettoGetResponse<
  BilletoEventOverviewType[]
> | null>;

// jscpd:ignore-start
export const fetchAllBillettoEvents = async ({
  state,
  startBefore,
  startsAfter,
  limit = 100,
  expand = "",
}: BillettoFetchOptionsType): FetchAllReturnType => {
  const { BILLETTO_API_KEY, BILLETTO_API_SECRET } = process.env;
  if (!BILLETTO_API_KEY || !BILLETTO_API_SECRET) {
    console.error("Billetto API key or secret is missing.");
    return null;
  }

  const url = `https://billetto.eu/api/v3/organiser/events?starts_after=${startsAfter?.toISOString() ?? ""}&start_before=${startBefore?.toISOString() ?? ""}&state=${state ?? ""}&expand=${expand}&limit=${limit}`;
  const options = {
    method: "GET",
    headers: {
      accept: "application/json",
      "Api-Keypair": `${BILLETTO_API_KEY}:${BILLETTO_API_SECRET}`,
    },
  } satisfies RequestInit;

  const fetchedData = await fetch(url, options);
  return fetchedData.json();
};

type LinkListElementType = Exclude<
  Modul_Linkliste["linklist"]["elements"],
  false
>;

export const mapBillettoEventToLinkListData = (
  data: BilletoEventOverviewType[],
  options: { icon: string; ariaLabel: string; linkTitle: string },
): LinkListElementType => {
  return data.map((event) => {
    const startingDate = new Date(event.starts_at).toLocaleDateString("de-DE", {
      year: "numeric",
      month: "long",
      day: "2-digit",
    });
    const tagline = `${startingDate}, ${event.location.postal_code} ${event.location.city}`;

    return {
      svg: {
        id: "",
        title: "calender icon",
        filename: "",
        url: options.icon,
        link: options.icon,
        alt: options.ariaLabel.replace("$eventTitle", event.name),
        author: "",
        name: "",
        caption: "",
        description: "",
        date: "",
        modified: "",
        mimetype: "image/svg+xml",
        mime_type: "image/svg+xml",
        image_blur: "",
        type: "image",
        width: 80,
        height: 80,
        acf: {
          link_1: "",
          link_2: "",
          text_1: "",
          text_2: "",
        },
      },
      title: event.name,
      tagline: tagline,
      link: {
        target: "",
        title: options.linkTitle,
        aria_label: "Öffne Link",
        url: `events/${event.id}`,
      },
      link_extended: {
        type: "link",
        link: null,
        download: {
          link_target: false,
          link_aria_label: "",
          link_text: "",
        },
        email: { link_target: "", link_aria_label: "", link_text: "" },
        pop_up: { link_text: "", link_aria_label: "" },
        telephone: { link_target: "", link_text: "", link_aria_label: "" },
      },
    };
  });
};

export const getBillettoEventDataById = async (
  eventId: string,
): Promise<BillettoEventType | null> => {
  const { BILLETTO_API_KEY, BILLETTO_API_SECRET } = process.env;
  if (!BILLETTO_API_KEY || !BILLETTO_API_SECRET) {
    console.error("Billetto API key or secret is missing.");
    return null;
  }

  const url = `https://billetto.eu/api/v3/organiser/events/${eventId}?expand=location,editorial,ticket_types`;
  const options = {
    method: "GET",
    headers: {
      accept: "application/json",
      "Api-Keypair": `${BILLETTO_API_KEY}:${BILLETTO_API_SECRET}`,
    },
  } satisfies RequestInit;

  const fetchedData = await fetch(url, options);
  return fetchedData.json();
};

export const mapBillettoImageToImageType = (
  id: string,
  name: string,
  imageUrl: string,
): ImageType => ({
  id: `image-event-${id}`,
  title: `image-event-${name}`,
  filename: `image-event-${name}`,
  link: imageUrl,
  url: imageUrl,
  alt: `image of event ${name}`,
  name,
  description: `image of event ${name}`,
  image_blur: "",
  width: 400,
  height: 400,
  acf: {
    link_1: "",
    link_2: "",
    text_1: "",
    text_2: "",
  },
});
// jscpd:ignore-end
